import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Button,
  Card,
  CardContent,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
} from '@mui/material';
import {
  People as PeopleIcon,
  Home as HomeIcon,
  Receipt as ReceiptIcon,
  AttachMoney as MoneyIcon,
  Warning as WarningIcon,
  Schedule as ScheduleIcon,
  Description as BillIcon,
  CalendarToday as CalendarIcon,
  TrendingUp as TrendingIcon,
  AccountBalance as DepositIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { PageHeader, Loading } from '../components/Layout';
import reportService from '../services/reportService';
import invoiceService from '../services/invoiceService';
import billService from '../services/billService';
import penaltyService from '../services/penaltyService';
import api from '../services/api';

// StatCard Component - Must be outside to avoid React error #130
const StatCard = ({ title, value, icon, color }) => (
  <Card>
    <CardContent>
      <Box display="flex" alignItems="center" justifyContent="space-between">
        <Box>
          <Typography color="textSecondary" gutterBottom variant="h6">
            {title}
          </Typography>
          <Typography variant="h4" component="h2">
            {value}
          </Typography>
        </Box>
        <Box sx={{ color: color, fontSize: 48 }}>
          {icon}
        </Box>
      </Box>
    </CardContent>
  </Card>
);

const Dashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalTenants: 0,
    totalRooms: 0,
    unpaidInvoices: 0,
    partialPaidInvoices: 0,
    overdueInvoices: 0,
    monthlyIncome: 0,
    yearlyIncome: 0,
    customPeriodIncome: 0,
    totalOutstanding: 0,
    monthlyDeposits: 0,
  });
  const [recentInvoices, setRecentInvoices] = useState([]);
  const [billStats, setBillStats] = useState({
    pendingBills: 0,
    partialPaidBills: 0,
    paidBills: 0,
    overdueBills: 0,
    totalPending: 0,
  });
  const [penalties, setPenalties] = useState({
    totalAmount: 0,
    totalCount: 0,
    tenantsWithPenalties: [],
  });
  const [dateFilter, setDateFilter] = useState({
    startDate: '',
    endDate: '',
  });

  useEffect(() => {
    fetchDashboardData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  
  useEffect(() => {
    // Recalculate custom period income when date filter changes
    if (dateFilter.startDate && dateFilter.endDate) {
      calculateCustomPeriodIncome();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateFilter]);

  const calculateCustomPeriodIncome = async () => {
    try {
      const paymentsRes = await api.get('/api/payments');
      const payments = paymentsRes.data || [];
      
      const startDate = new Date(dateFilter.startDate);
      const endDate = new Date(dateFilter.endDate);
      endDate.setHours(23, 59, 59, 999);
      
      const customIncome = payments.reduce((sum, payment) => {
        const paymentDate = new Date(payment.payment_date);
        if (paymentDate >= startDate && paymentDate <= endDate) {
          return sum + (parseFloat(payment.amount) || 0);
        }
        return sum;
      }, 0);
      
      setStats(prev => ({ ...prev, customPeriodIncome: customIncome }));
    } catch (error) {
      console.error('Error calculating custom period income:', error);
    }
  };

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      // Fetch dashboard stats, bill statistics, and penalties summary in parallel
      const [statsRes, billStatsRes, penaltiesRes] = await Promise.all([
        reportService.getDashboardStats(),
        billService.getBillStatistics().catch(() => ({ data: {} })),
        penaltyService.getPenaltiesSummary().catch(() => ({ data: { total_amount: 0, total_penalties: 0, tenants_with_penalties: [] } })),
      ]);

      const dashboardStats = statsRes.data || {};
      const billStatistics = billStatsRes.data || {};

      // Use stats from backend directly (no need to fetch all invoices)
      const partialPaidCount = dashboardStats.partial_paid_invoices || 0;
      const totalOutstanding = dashboardStats.total_outstanding || 0;
      
      // Recent outstanding invoices come from backend stats
      const recentInvoicesList = (dashboardStats.recent_outstanding || []);

      // Calculate income from payments for different periods
      const now = new Date();
      const currentMonth = now.getMonth();
      const currentYear = now.getFullYear();
      
      // Fetch deposits to calculate monthly deposits received
      let monthlyDepositsReceived = 0;
      try {
        const depositsRes = await api.get('/api/deposits');
        const deposits = depositsRes.data || [];
        
        // Calculate monthly deposits (current month)
        monthlyDepositsReceived = deposits.reduce((sum, deposit) => {
          const depositDate = new Date(deposit.payment_date);
          if (depositDate.getMonth() === currentMonth && depositDate.getFullYear() === currentYear) {
            return sum + (parseFloat(deposit.amount) || 0);
          }
          return sum;
        }, 0);
      } catch (error) {
        console.error('Error fetching deposits:', error);
      }
      
      // Fetch payments to calculate income
      let monthlyIncomeFromPayments = 0;
      let yearlyIncomeFromPayments = 0;
      let customPeriodIncomeFromPayments = 0;
      
      try {
        const paymentsRes = await api.get('/api/payments');
        const payments = paymentsRes.data || [];
        
        // Calculate monthly income (current month)
        monthlyIncomeFromPayments = payments.reduce((sum, payment) => {
          const paymentDate = new Date(payment.payment_date);
          if (paymentDate.getMonth() === currentMonth && paymentDate.getFullYear() === currentYear) {
            return sum + (parseFloat(payment.amount) || 0);
          }
          return sum;
        }, 0);
        
        // Calculate yearly income (current year)
        yearlyIncomeFromPayments = payments.reduce((sum, payment) => {
          const paymentDate = new Date(payment.payment_date);
          if (paymentDate.getFullYear() === currentYear) {
            return sum + (parseFloat(payment.amount) || 0);
          }
          return sum;
        }, 0);
        
        // Calculate custom period income if dates are set
        if (dateFilter.startDate && dateFilter.endDate) {
          const startDate = new Date(dateFilter.startDate);
          const endDate = new Date(dateFilter.endDate);
          endDate.setHours(23, 59, 59, 999); // Include the entire end date
          
          customPeriodIncomeFromPayments = payments.reduce((sum, payment) => {
            const paymentDate = new Date(payment.payment_date);
            if (paymentDate >= startDate && paymentDate <= endDate) {
              return sum + (parseFloat(payment.amount) || 0);
            }
            return sum;
          }, 0);
        }
      } catch (error) {
        console.error('Error fetching payments for income calculations:', error);
      }
      
      // Use backend monthly income if available, otherwise use calculated monthly income from payments
      const displayMonthlyIncome = dashboardStats.monthly_income > 0 
        ? dashboardStats.monthly_income 
        : monthlyIncomeFromPayments;
      
      // Use backend stats for counts that don't need penalty adjustment
      // Override unpaid count and outstanding balance with our calculations
      setStats({
        totalTenants: dashboardStats.total_tenants || 0,
        totalRooms: dashboardStats.total_rooms || 0,
        unpaidInvoices: dashboardStats.unpaid_invoices || 0,
        partialPaidInvoices: partialPaidCount,
        overdueInvoices: dashboardStats.overdue_invoices || 0,
        monthlyIncome: displayMonthlyIncome,
        yearlyIncome: yearlyIncomeFromPayments,
        customPeriodIncome: customPeriodIncomeFromPayments,
        totalOutstanding: totalOutstanding,
        monthlyDeposits: monthlyDepositsReceived,
      });
      setRecentInvoices(recentInvoicesList);
      // Use backend-calculated bill statistics which properly handle partial_paid bills
      setBillStats({
        pendingBills: billStatistics.pending_bills || 0,
        partialPaidBills: billStatistics.partial_paid_bills || 0,
        paidBills: billStatistics.paid_bills || 0,
        overdueBills: billStatistics.overdue_bills || 0,
        totalPending: billStatistics.total_pending_amount || 0,
      });
      
      // Set penalties data
      const penaltiesSummary = penaltiesRes.data;
      setPenalties({
        totalAmount: penaltiesSummary.total_amount || 0,
        totalCount: penaltiesSummary.total_penalties || 0,
        tenantsWithPenalties: penaltiesSummary.tenants_with_penalties || [],
      });
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <Loading message="Loading dashboard..." />;
  }

  return (
    <Box>
      <PageHeader
        title="Dashboard"
        subtitle="Overview of your property management system"
      />

      {/* Statistics Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Total Tenants"
              value={stats.totalTenants}
              icon={<PeopleIcon fontSize="inherit" />}
              color="primary.main"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Total Rooms"
              value={stats.totalRooms}
              icon={<HomeIcon fontSize="inherit" />}
              color="success.main"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Unpaid Invoices"
              value={stats.unpaidInvoices}
              icon={<ReceiptIcon fontSize="inherit" />}
              color="warning.main"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Monthly Income"
              value={`KES ${stats.monthlyIncome.toLocaleString()}`}
              icon={<MoneyIcon fontSize="inherit" />}
              color="success.main"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Yearly Income"
              value={`KES ${stats.yearlyIncome.toLocaleString()}`}
              icon={<TrendingIcon fontSize="inherit" />}
              color="info.main"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Monthly Deposits"
              value={`KES ${stats.monthlyDeposits.toLocaleString()}`}
              icon={<DepositIcon fontSize="inherit" />}
              color="secondary.main"
            />
          </Grid>
      </Grid>

      {/* Date Filter Section */}
      <Paper sx={{ p: 3, mb: 4 }}>
        <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
          <CalendarIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          Income by Custom Period
        </Typography>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={4}>
            <TextField
              label="Start Date"
              type="date"
              fullWidth
              value={dateFilter.startDate}
              onChange={(e) => setDateFilter({ ...dateFilter, startDate: e.target.value })}
              InputLabelProps={{ shrink: true }}
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              label="End Date"
              type="date"
              fullWidth
              value={dateFilter.endDate}
              onChange={(e) => setDateFilter({ ...dateFilter, endDate: e.target.value })}
              InputLabelProps={{ shrink: true }}
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <Card sx={{ bgcolor: '#e8f5e9', height: '100%' }}>
              <CardContent sx={{ py: 1.5 }}>
                <Typography variant="body2" color="textSecondary" gutterBottom>
                  Period Income
                </Typography>
                <Typography variant="h5" color="success.main" fontWeight="bold">
                  KES {stats.customPeriodIncome.toLocaleString()}
                </Typography>
                {dateFilter.startDate && dateFilter.endDate && (
                  <Typography variant="caption" color="textSecondary">
                    {new Date(dateFilter.startDate).toLocaleDateString()} - {new Date(dateFilter.endDate).toLocaleDateString()}
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Paper>

      {/* Invoice Details Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ bgcolor: '#fff3e0' }}>
              <CardContent>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography color="textSecondary" gutterBottom variant="body2">
                      Overdue Invoices
                    </Typography>
                    <Typography variant="h4" component="h2" color="error">
                      {stats.overdueInvoices}
                    </Typography>
                  </Box>
                  <WarningIcon sx={{ fontSize: 40, color: 'error.main' }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ bgcolor: '#e3f2fd' }}>
              <CardContent>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography color="textSecondary" gutterBottom variant="body2">
                      Partial Paid
                    </Typography>
                    <Typography variant="h4" component="h2" color="info.main">
                      {stats.partialPaidInvoices}
                    </Typography>
                  </Box>
                  <ScheduleIcon sx={{ fontSize: 40, color: 'info.main' }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={6}>
            <Card sx={{ bgcolor: '#ffebee' }}>
              <CardContent>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography color="textSecondary" gutterBottom variant="body2">
                      Total Outstanding Balance
                    </Typography>
                    <Typography variant="h4" component="h2" color="error.main">
                      KES {stats.totalOutstanding.toLocaleString()}
                    </Typography>
                  </Box>
                  <MoneyIcon sx={{ fontSize: 40, color: 'error.main' }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
      </Grid>

      {/* Welcome Message */}
      <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h5" gutterBottom>
            Welcome to CISTenant! 👋
          </Typography>
          <Typography variant="body1" paragraph>
            Your property management system is ready. Get started by adding tenants and rooms.
          </Typography>
          <Box sx={{ mt: 2 }}>
            <Button variant="contained" sx={{ mr: 2 }} onClick={() => navigate('/tenants')}>
              Manage Tenants
            </Button>
            <Button variant="outlined" onClick={() => navigate('/rooms')}>
              Manage Rooms
            </Button>
          </Box>
      </Paper>

      {/* Late Payment Penalties */}
      {penalties.tenantsWithPenalties.length > 0 && (
        <Paper sx={{ p: 3, mb: 3, bgcolor: '#fff3e0', border: '2px solid', borderColor: 'warning.main' }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Box>
              <Typography variant="h6" color="error.main">
                ⚠️ Late Payment Penalties
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {penalties.totalCount} penalt{penalties.totalCount > 1 ? 'ies' : 'y'} totaling KES {penalties.totalAmount.toLocaleString()}
              </Typography>
            </Box>
          </Box>
          <Grid container spacing={2}>
            {penalties.tenantsWithPenalties.map((tenant) => (
              <Grid item xs={12} sm={6} md={4} key={tenant.tenant_id}>
                <Card sx={{ border: '1px solid', borderColor: 'error.light' }}>
                  <CardContent>
                    <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                      {tenant.tenant_name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Room: {tenant.room_number}
                    </Typography>
                    {tenant.invoice_number && (
                      <Typography variant="body2" color="primary.main" gutterBottom>
                        Invoice No: #{tenant.invoice_number}
                      </Typography>
                    )}
                    <Box display="flex" justifyContent="space-between" alignItems="center" mt={1}>
                      <Typography variant="body2" color="text.secondary">
                        {tenant.penalty_count} penalt{tenant.penalty_count > 1 ? 'ies' : 'y'}
                      </Typography>
                      <Typography variant="h6" color="error.main" fontWeight="bold">
                        KES {tenant.total_penalty_amount.toLocaleString()}
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Paper>
      )}

      {/* Utility Bills Overview */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6">
            <BillIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
            Utility Bills Overview
          </Typography>
          <Button variant="outlined" size="small" onClick={() => navigate('/bills')}>
            Manage Bills
          </Button>
        </Box>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={2.4}>
            <Card sx={{ bgcolor: '#fff3e0' }}>
              <CardContent>
                <Typography color="textSecondary" variant="body2">
                  Pending Bills
                </Typography>
                <Typography variant="h5" color="warning.main">
                  {billStats.pendingBills}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={2.4}>
            <Card sx={{ bgcolor: '#e3f2fd' }}>
              <CardContent>
                <Typography color="textSecondary" variant="body2">
                  Partial Paid
                </Typography>
                <Typography variant="h5" color="info.main">
                  {billStats.partialPaidBills}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={2.4}>
            <Card sx={{ bgcolor: '#ffebee' }}>
              <CardContent>
                <Typography color="textSecondary" variant="body2">
                  Overdue Bills
                </Typography>
                <Typography variant="h5" color="error.main">
                  {billStats.overdueBills}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={2.4}>
            <Card sx={{ bgcolor: '#e8f5e9' }}>
              <CardContent>
                <Typography color="textSecondary" variant="body2">
                  Paid Bills
                </Typography>
                <Typography variant="h5" color="success.main">
                  {billStats.paidBills}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={2.4}>
            <Card sx={{ bgcolor: '#f3e5f5' }}>
              <CardContent>
                <Typography color="textSecondary" variant="body2">
                  Total Pending Amount
                </Typography>
                <Typography variant="h6" color="secondary.main">
                  KES {billStats.totalPending.toLocaleString()}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Paper>

      {/* Invoice Details and Quick Actions */}
      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Outstanding Invoices
            </Typography>
            {recentInvoices.length === 0 ? (
              <Typography color="textSecondary">
                No outstanding invoices
              </Typography>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Invoice #</TableCell>
                      <TableCell>Due Date</TableCell>
                      <TableCell>Amount</TableCell>
                      <TableCell>Paid</TableCell>
                      <TableCell>Balance</TableCell>
                      <TableCell>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {recentInvoices.map((invoice) => (
                      <TableRow key={invoice.id}>
                        <TableCell>{invoice.invoice_number}</TableCell>
                        <TableCell>
                          {new Date(invoice.due_date).toLocaleDateString()}
                        </TableCell>
                        <TableCell>KES {parseFloat(invoice.amount).toLocaleString()}</TableCell>
                        <TableCell>KES {parseFloat(invoice.amount_paid || 0).toLocaleString()}</TableCell>
                        <TableCell>
                          <Typography color="error" fontWeight="bold">
                            KES {invoice.remaining.toLocaleString()}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={invoice.status.replace('_', ' ').toUpperCase()}
                            size="small"
                            color={
                              invoice.status === 'overdue' ? 'error' :
                              invoice.status === 'partial_paid' ? 'info' : 'warning'
                            }
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
            <Box sx={{ mt: 2, textAlign: 'right' }}>
              <Button variant="outlined" onClick={() => navigate('/invoices')}>
                View All Invoices
              </Button>
            </Box>
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Quick Actions
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Button variant="text" fullWidth sx={{ justifyContent: 'flex-start' }} onClick={() => navigate('/invoices')}>
                Generate Monthly Invoices
              </Button>
              <Button variant="text" fullWidth sx={{ justifyContent: 'flex-start' }} onClick={() => navigate('/reports')}>
                View Reports
              </Button>
              <Button variant="text" fullWidth sx={{ justifyContent: 'flex-start' }} onClick={() => navigate('/messaging')}>
                Send Reminders
              </Button>
              <Button variant="text" fullWidth sx={{ justifyContent: 'flex-start' }} onClick={() => navigate('/tenants')}>
                Manage Tenants
              </Button>
              <Button variant="text" fullWidth sx={{ justifyContent: 'flex-start' }} onClick={() => navigate('/rooms')}>
                Manage Rooms
              </Button>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Dashboard;
